/* eslint-disable @typescript-eslint/ban-ts-comment */
import { LocalFind, LocalInsert, LocalRemove } from '@weblite/persist'
// @ts-ignore
import {
  GetChangelogs,
  reqGetChangelogs,
  Changelog,
} from '@weblite/ts-action/admin'
import { Timestamp } from '@weblite/ts-action/types'
import {
  isAndroid,
  isAndroidApp,
  isInTwa,
  isInWebView,
  OS_VERSION,
  twaVersion,
} from '$func/environmentInfo'
import { request } from '$setup/ws'
import { showChangelogModal } from '$comp/modals/changelog'
import { showRecommendUpdateTwaDialog } from '$comp/modals/permission-dialog'
import { weekInMilliSeconds } from '$func/time'
import { VERSION } from '$setup/version'
import { TWA_API_SUPPORT } from '$logic/twaCommunication'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import {
  WARNING_PAGES,
  getBrowserCompatibilityWarningPage,
} from '$setup/error-assistant'
import { showBrowserNotSupported } from '$comp/modals/browser-not-supported'
import { showOutDatedBrowser } from '$comp/modals/out-dated-browser'
import { showOutDatedOs } from '$comp/modals/out-dated-os'
import { clientSettingsStorage, getCurrentRelease } from './storage'
import { areStartupDialogsDisabled } from '$logic/router/helper'
import { getClientUserId } from '$logic/client-user'
import {
  checkIsOsCompatibleWithTwa,
  checkIsOsCompatibleWithWebView,
} from '$logic/client-device/helper'
import { showRecommendInstallTwaModal } from '$comp/modals/recommend-install-twa'
import {
  FORCE_INSTALL_TWA_ENABLED,
  RECOMMEND_INSTALL_TWA_ENABLED,
} from '$config/env'
import { StartUpModalQueue } from '$func/startup-modal-handler'
import { StartupModalPriorities } from '$helper/constants/startup-modal-priorities'
import { getUserPermissionAndRegisterNotificationInNonAndroidDevices } from '$setup/notification'

// @ts-ignore
export const TWA_VERSION = import.meta.env.VITE_TWA_VERSION

export function twaUpdateIsAvailable() {
  return twaVersion && twaVersion < TWA_VERSION && !needForceUpdateTwa()
}

export function checkRecommendUpdateTwa() {
  if (twaUpdateIsAvailable())
    StartUpModalQueue.add({
      onOpen: showRecommendUpdateTwaDialog,
      priority: StartupModalPriorities.updateTwaAvailable,
    })
}

// @ts-ignore
const downloadTwaSrc = import.meta.env.VITE_TWA_DOWNLOAD_SRC
export const needForceUpdateTwa = () => {
  return (
    twaVersion && downloadTwaSrc && +twaVersion < TWA_API_SUPPORT.LEAST_VERSION
  )
}

const twaCanBeInstalled = () => {
  if (areStartupDialogsDisabled()) return false
  if (!getClientUserId()) return false
  if (!isAndroid) return false
  if (!checkIsOsCompatibleWithTwa(OS_VERSION)) return false
  // if (!checkIsOsCompatibleWithWebView(OS_VERSION)) return false // TODO-WEBVIEW: enable later
  if (!isInTwa) return true
  // if (!isAndroidApp) return true // TODO-WEBVIEW: enable later
  return false
}

export function needRecommendInstallTwa(): boolean {
  if (!RECOMMEND_INSTALL_TWA_ENABLED) return false
  return twaCanBeInstalled()
}

export function needForceInstallTwa() {
  if (!FORCE_INSTALL_TWA_ENABLED) return false
  return twaCanBeInstalled()
}

export const checkNeedInstallTwaModal = () => {
  if (needRecommendInstallTwa()) showRecommendInstallTwaModal()
}

function shouldIgnoreForcePromotion() {
  const { time, version } = LocalFind('ignoreInstallPromotion') || {}
  if (!time) return false

  return !(
    time + weekInMilliSeconds < dateWithOffset() || version !== TWA_VERSION
  )
}

export function ignoreInstallPromotion() {
  LocalInsert('ignoreInstallPromotion', {
    time: dateWithOffset(),
    version: TWA_VERSION,
  })
}

async function getAppReleasesAfter(date: Timestamp) {
  const res = await request<GetChangelogs>(
    reqGetChangelogs({
      ...(date && { after: date }),
      before: dateWithOffset(),
    }),
    { retry: -1 },
  )
  if (res.error) {
    console.error(res.error.message)
    return []
  }
  return res.payload.changelogs.map(({ date: isoDate, ...other }) => {
    return { ...other, date: new Date(isoDate) }
  })
}

let cachedNewReleases: Changelog[] = null
export async function getAppNewReleases() {
  if (cachedNewReleases) return cachedNewReleases
  const { releaseTime } = getCurrentRelease()
  const res = await getAppReleasesAfter(releaseTime)
  // @ts-ignore
  cachedNewReleases = res
  return res
}

export async function checkNewAppVersionInstalled() {
  if (VERSION === getCurrentRelease()?.version) return
  const releaseVersions = await getAppNewReleases()

  if (!releaseVersions?.length || areStartupDialogsDisabled()) return
  StartUpModalQueue.add({
    onOpen: showChangelogModal,
    priority: StartupModalPriorities.changeLog,
  })
}

export const checkBrowserCompatibility = () => {
  if (
    window.config.frontend.VITE_CHECK_BROWSER_COMPATIBILITY_ON_LOAD === 'true'
  )
    return
  if (isInWebView) return // TODO-WEBVIEW: enable later
  const warningPage = getBrowserCompatibilityWarningPage(true)
  if (!warningPage) return

  if (warningPage === WARNING_PAGES.browserNotSupported)
    showBrowserNotSupported()
  else if (warningPage === WARNING_PAGES.outdatedBrowser)
    StartUpModalQueue.add({
      onOpen: showOutDatedBrowser,
      priority: StartupModalPriorities.outOfDateBrowser,
    })
  else if (warningPage === WARNING_PAGES.outdatedOs)
    StartUpModalQueue.add({
      onOpen: showOutDatedOs,
      priority: StartupModalPriorities.outOfDateOs,
    })
}

// CLIENT SETTINGS STORAGE
export async function setAutoDownload(enable: boolean) {
  await clientSettingsStorage.set({ autoDownload: enable })
}

export async function setAutoPlay(enable: boolean) {
  await clientSettingsStorage.set({ autoPlayAudio: enable })
}

export async function setAutoSyncContacts(enable: boolean) {
  await clientSettingsStorage.set({ autoSyncContacts: enable })
}

export async function setRecentUsersIsEnabled(enable: boolean) {
  await clientSettingsStorage.set({ recentUsers: enable })
}

const SYNCHRONIZE_PERSIST = 'synchronizePersist'

export function storeDBEssentialsOnLocalStorage() {
  const clientStorage = clientSettingsStorage.get()
  return LocalInsert(SYNCHRONIZE_PERSIST, JSON.stringify(clientStorage))
}

export async function restoreDBEssentialsFromLocalStorage() {
  const parsed = JSON.parse(LocalFind(SYNCHRONIZE_PERSIST))
  if (parsed) {
    LocalRemove(SYNCHRONIZE_PERSIST)
    return clientSettingsStorage.set(parsed)
  }
}

export const checkBrowserNotificationPermission = () => {
  console.log('checkBrowserNotificationPermission')
  StartUpModalQueue.add({
    onOpen: getUserPermissionAndRegisterNotificationInNonAndroidDevices,
    priority: StartupModalPriorities.nonTwaNotificationPermission,
    nextOneIsManual: true,
  })
}
